/**
 * ReferenceLibraryContent - Engineer's Technical Reference Library
 * 
 * External technical references for engineering work:
 * - Industry Standards (DO-178C, AS9100D, ARP4761...)
 * - Advisory Circulars (FAA guidance)
 * - Guidance Documents (implementation guides)
 * - Specifications (technical specs)
 * 
 * Note: This is separate from Document Control (AS9100D managed company docs)
 * 
 * Features:
 * - Show first 5 per category with "Show all" expand (matches Doc Control style)
 * - AI-generated summaries on document cards
 * - Clickable related document tags (Neptune graph)
 * - RAG-powered search
 */

import { useState, useEffect } from 'react';
import { logger } from '../../utils/logger';
import type { 
  ReferenceDocumentCard, 
  RecentDocumentGroup,
  ReferenceScopeType,
} from '../../types/panel-types';
import { SCOPE_TYPE_INFO } from '../../types/panel-types';
import { projectsApi, api, referenceDocumentsApi } from '../../services/api';
import { 
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Label,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
// ProjectInstance type removed - no longer needed after simplification
import PdfPasswordModal from '../reference/PdfPasswordModal';
import ReferenceEditModal from '../reference/ReferenceEditModal';

const ITEMS_TO_SHOW = 5;
const RECENT_DOCS_KEY = 'aeroRecentDocuments';

/**
 * Extract standard number from text (snippet, title, or summary)
 * Looks for patterns like "ARP4761", "DO-178C", "AS9100D", etc.
 */
function extractStandardFromText(text?: string): string | null {
  if (!text) return null;
  
  // Common standard patterns:
  const patterns = [
    /\b(ARP\s*\d+[A-Z]?)\b/i,           // ARP4761, ARP 4754A
    /\b(DO-?\d+[A-Z]?)\b/i,              // DO-178C, DO178C
    /\b(AS\d+[A-Z]?)\b/i,                // AS9100D
    /\b(MIL-STD-\d+[A-Z]?)\b/i,          // MIL-STD-882
    /\b(ED-\d+[A-Z]?)\b/i,               // ED-12C (EASA equivalent)
    /\b(RTCA\/DO-?\d+[A-Z]?)\b/i,        // RTCA/DO-178C
    /\b(AC\s*\d+-\d+[A-Z]?)\b/i,         // AC 20-115D (Advisory Circulars)
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      // Clean up the match (remove extra spaces, normalize)
      return match[1].replace(/\s+/g, '').toUpperCase();
    }
  }
  
  return null;
}

interface ReferenceLibraryContentProps {
  showSearchResults: boolean;
  searchResults: ReferenceDocumentCard[];
  recentGroups: RecentDocumentGroup[];
  loading: boolean;
  isSearching: boolean;
  onDocumentClick: (doc: ReferenceDocumentCard) => void;
  onRelatedTagClick: (tag: { documentId: string; title: string; standardNumber?: string }) => void;
  onToggleGroup: (scopeType: ReferenceScopeType) => void;
  onRefresh?: () => void;
}

export function ReferenceLibraryContent({
  showSearchResults,
  searchResults,
  recentGroups,
  loading,
  isSearching,
  onDocumentClick,
  onRelatedTagClick,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onToggleGroup,
  onRefresh,
}: ReferenceLibraryContentProps) {
  // Track which categories show all items
  const [showAllMap, setShowAllMap] = useState<Record<string, boolean>>({});
  const [recentDocIds, setRecentDocIds] = useState<string[]>([]);
  
  // Organization filter (kept for additional filtering)
  const [selectedOrgFilter, setSelectedOrgFilter] = useState<string>('all');
  
  // Password modal state for password-protected PDFs
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [docNeedingPassword, setDocNeedingPassword] = useState<ReferenceDocumentCard | null>(null);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState<ReferenceDocumentCard | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Reindex state
  const [isReindexing, setIsReindexing] = useState<string | null>(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [docToEdit, setDocToEdit] = useState<ReferenceDocumentCard | null>(null);
  
  // Suppress unused import warnings
  void projectsApi;
  void api;
  
  const openPasswordModal = (doc: ReferenceDocumentCard, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocNeedingPassword(doc);
    setShowPasswordModal(true);
  };
  
  // Delete document handlers
  const openDeleteModal = (doc: ReferenceDocumentCard, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocToDelete(doc);
    setShowDeleteModal(true);
  };
  
  const handleDelete = async () => {
    if (!docToDelete) return;
    
    setIsDeleting(true);
    try {
      await referenceDocumentsApi.delete(docToDelete.documentId);
      setShowDeleteModal(false);
      setDocToDelete(null);
      // Refresh document list
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      logger.error('Failed to delete document:', err);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Reindex document handler
  const handleReindex = async (doc: ReferenceDocumentCard, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsReindexing(doc.documentId);
    try {
      await referenceDocumentsApi.reindex(doc.documentId);
      // Refresh document list to show new status
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      logger.error('Failed to reindex document:', err);
    } finally {
      setIsReindexing(null);
    }
  };
  
  // Edit document handler
  const openEditModal = (doc: ReferenceDocumentCard, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocToEdit(doc);
    setShowEditModal(true);
  };
  
  const openLinkModal = (doc: ReferenceDocumentCard, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocToLink(doc);
    setSelectedProjectId('');
    setLinkSuccess(null);
    setShowLinkModal(true);
  };
  
  // Extract unique organizations from documents
  const allOrganizations = Array.from(new Set(
    recentGroups.flatMap(g => g.documents.map(d => d.organization).filter(Boolean))
  )).sort() as string[];
  
  // Load and sync recent documents from localStorage
  useEffect(() => {
    const loadRecentDocs = () => {
      try {
        const stored = localStorage.getItem(RECENT_DOCS_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            // Filter for reference documents only (type 'standard' or ref- prefix)
            type StoredItem = string | { id?: string; type?: string };
            const refDocIds = parsed
              .filter((item: StoredItem) => {
                if (typeof item === 'string') return item.startsWith('ref-');
                if (item && typeof item === 'object') {
                  return item.type === 'standard' || item.id?.startsWith('ref-');
                }
                return false;
              })
              .map((item: StoredItem) => typeof item === 'string' ? item : item.id);
            setRecentDocIds(refDocIds);
          }
        }
      } catch {
        // Ignore parse errors
      }
    };
    
    loadRecentDocs();
    
    // Poll for updates
    const interval = setInterval(loadRecentDocs, 2000);
    
    // Listen for storage events from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === RECENT_DOCS_KEY) {
        loadRecentDocs();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Get all documents from all groups for recent matching
  const allDocuments = recentGroups.flatMap(g => g.documents);
  
  // Get recent documents by matching IDs
  const recentDocuments = recentDocIds
    .map(recentId => {
      // Try matching by full ID or stripped ref- prefix
      const docId = recentId.replace(/^ref-/, '');
      return allDocuments.find(d => 
        d.documentId === recentId || 
        d.documentId === docId ||
        d.documentId?.endsWith(docId)
      );
    })
    .filter((d): d is ReferenceDocumentCard => d !== undefined)
    .slice(0, 5);
  
  const toggleShowAll = (scopeType: string) => {
    setShowAllMap(prev => ({
      ...prev,
      [scopeType]: !prev[scopeType],
    }));
  };
  
  // Loading state
  if (loading && !showSearchResults) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Search results view
  if (showSearchResults) {
    if (isSearching) {
      return (
        <div key="search-loading" className="p-8 text-center">
          <div className="animate-spin text-3xl mb-3">🔍</div>
          <p className="text-muted-foreground">Searching with AI...</p>
        </div>
      );
    }
    
    if (searchResults.length === 0) {
      return (
        <div key="search-empty" className="p-8 text-center">
          <div className="text-4xl mb-3">📭</div>
          <p className="text-muted-foreground">No results found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try different keywords or a more specific query
          </p>
        </div>
      );
    }
    
    // Group search results by document type
    const groupedResults = groupByDocumentType(searchResults);
    
    return (
      <div key="search-results" className="p-4 space-y-4">
        <div className="text-sm text-muted-foreground mb-2">
          Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
        </div>
        
        {groupedResults.map(group => (
          <DocumentGroup
            key={group.scopeType}
            group={group}
            showAll={showAllMap[group.scopeType] || false}
            onToggleShowAll={() => toggleShowAll(group.scopeType)}
            onDocumentClick={onDocumentClick}
            onRelatedTagClick={onRelatedTagClick}
            onLinkToProject={openLinkModal}
            onPasswordRequired={openPasswordModal}
            onDelete={openDeleteModal}
            onReindex={handleReindex}
            onEdit={openEditModal}
            reindexingDocId={isReindexing}
            showRelevanceScore={true}
          />
        ))}
      </div>
    );
  }
  
  // Recent documents view
  if (recentGroups.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">📚</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No reference documents yet
        </h3>
        <p className="text-muted-foreground mb-4">
          Upload your first reference document to get started
        </p>
        <p className="text-sm text-muted-foreground">
          Drag and drop a PDF, DOCX, XLSX, or TXT file, or click the Upload button
        </p>
      </div>
    );
  }
  
  // Reference Library ONLY shows "All Projects" documents (customer-wide standards)
  // Project-specific documents are managed in Project References
  const filteredGroups = recentGroups
    .map(group => ({
      ...group,
      documents: group.documents.filter(doc => {
        // Only show "All Projects" scope (no assigned projects = customer-wide)
        const isAllProjects = !doc.assignedProjects || doc.assignedProjects.length === 0;
        
        // Filter by organization
        const matchesOrg = selectedOrgFilter === 'all' || doc.organization === selectedOrgFilter;
        
        return isAllProjects && matchesOrg;
      })
    }))
    .filter(group => group.documents.length > 0);
  
  const hasActiveFilters = selectedOrgFilter !== 'all';
  
  return (
    <div key="default-view" className="p-4 space-y-4">
      {/* Header: Customer-wide standards info + Organization filter */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm text-muted-foreground">
          Customer-wide standards available to all projects
        </div>
        
        {/* Organization filter */}
        <div className="flex items-center gap-2">
          {allOrganizations.length > 0 && (
            <>
              <Label className="text-xs text-muted-foreground">Org:</Label>
              <RadixSelect
                value={selectedOrgFilter}
                onValueChange={setSelectedOrgFilter}
              >
                <SelectTrigger className="h-8 text-sm w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {allOrganizations.map(org => (
                    <SelectItem key={org} value={org}>{org}</SelectItem>
                  ))}
                </SelectContent>
              </RadixSelect>
            </>
          )}
          
          {hasActiveFilters && (
            <Button
              variant="link"
              size="sm"
              onClick={() => setSelectedOrgFilter('all')}
              className="text-xs h-auto p-0"
            >
              Clear filter
            </Button>
          )}
        </div>
      </div>
      
      {/* Recently Opened Section */}
      {recentDocuments.length > 0 && !hasActiveFilters && (
        <div className="border border-info bg-info-light rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            🕐 Recently Opened
          </h3>
          <div className="flex flex-wrap gap-2">
            {recentDocuments.map(doc => {
              // Get icon based on document type
              const typeInfo = SCOPE_TYPE_INFO[doc.documentType as ReferenceScopeType];
              const icon = typeInfo?.icon || '📄';
              const displayName = doc.standardNumber || doc.title || doc.documentId;
              
              return (
                <Button
                  key={doc.documentId}
                  variant="ghost"
                  size="sm"
                  onClick={() => onDocumentClick(doc)}
                  className="flex items-center gap-1.5 h-auto py-1.5 bg-muted/30 hover:bg-muted/50"
                >
                  <span>{icon}</span>
                  <span className="text-foreground truncate max-w-[180px]">
                    {displayName}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Document Groups by Type */}
      {filteredGroups.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <div className="text-4xl mb-3">📚</div>
          <p className="font-medium text-foreground mb-1">
            No customer-wide standards yet
          </p>
          <p className="text-sm">
            {hasActiveFilters
              ? 'No documents match your filter. Try clearing the filter.'
              : 'Upload industry standards like DO-178C, ARP4761, or AS9100D'
            }
          </p>
          {hasActiveFilters && (
            <Button
              variant="link"
              size="sm"
              onClick={() => setSelectedOrgFilter('all')}
              className="mt-2"
            >
              Clear organization filter
            </Button>
          )}
        </div>
      ) : (
        filteredGroups.map(group => (
          <DocumentGroup
            key={group.scopeType}
            group={group}
            showAll={showAllMap[group.scopeType] || false}
            onToggleShowAll={() => toggleShowAll(group.scopeType)}
            onDocumentClick={onDocumentClick}
            onRelatedTagClick={onRelatedTagClick}
            onLinkToProject={openLinkModal}
            onPasswordRequired={openPasswordModal}
            onDelete={openDeleteModal}
            onReindex={handleReindex}
            onEdit={openEditModal}
            reindexingDocId={isReindexing}
            showRelevanceScore={false}
          />
        ))
      )}
      
      
      {/* Password Modal for encrypted PDFs */}
      {showPasswordModal && docNeedingPassword && (
        <PdfPasswordModal
          document={docNeedingPassword}
          onClose={() => {
            setShowPasswordModal(false);
            setDocNeedingPassword(null);
          }}
          onSuccess={() => {
            setShowPasswordModal(false);
            setDocNeedingPassword(null);
            // Refresh document list
            if (onRefresh) {
              onRefresh();
            }
          }}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal && !!docToDelete} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteModal(false);
          setDocToDelete(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Reference Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{docToDelete?.title || docToDelete?.standardNumber || 'this document'}"? 
              This will remove the document and all indexed content. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setDocToDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Metadata Modal */}
      {showEditModal && docToEdit && (
        <ReferenceEditModal
          document={docToEdit}
          onClose={() => {
            setShowEditModal(false);
            setDocToEdit(null);
          }}
          onSuccess={() => {
            if (onRefresh) {
              onRefresh();
            }
          }}
        />
      )}
    </div>
  );
}

// Helper to group search results by document type
function groupByDocumentType(results: ReferenceDocumentCard[]): RecentDocumentGroup[] {
  const groups: Record<ReferenceScopeType, ReferenceDocumentCard[]> = {
    'industry-standard': [],
    'advisory-circular': [],
    'guidance': [],
    'specification': [],
  };
  
  results.forEach(doc => {
    const type = doc.documentType || 'industry-standard';
    if (groups[type]) {
      groups[type].push(doc);
    }
  });
  
  return Object.entries(groups)
    .filter(([, docs]) => docs.length > 0)
    .map(([type, docs]) => ({
      scopeType: type as ReferenceScopeType,
      label: SCOPE_TYPE_INFO[type as ReferenceScopeType]?.label || type,
      icon: SCOPE_TYPE_INFO[type as ReferenceScopeType]?.icon || '📄',
      documents: docs,
      isExpanded: true,
    }));
}

// Document group component (matches Document Control styling)
interface DocumentGroupProps {
  group: RecentDocumentGroup;
  showAll: boolean;
  onToggleShowAll: () => void;
  onDocumentClick: (doc: ReferenceDocumentCard) => void;
  onRelatedTagClick: (tag: { documentId: string; title: string; standardNumber?: string }) => void;
  onLinkToProject?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onPasswordRequired?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onDelete?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onReindex?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onEdit?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  reindexingDocId?: string | null;
  showRelevanceScore: boolean;
}

function DocumentGroup({ 
  group, 
  showAll,
  onToggleShowAll,
  onDocumentClick, 
  onRelatedTagClick,
  onLinkToProject,
  onPasswordRequired,
  onDelete,
  onReindex,
  onEdit,
  reindexingDocId,
  showRelevanceScore,
}: DocumentGroupProps) {
  const visibleDocs = showAll ? group.documents : group.documents.slice(0, ITEMS_TO_SHOW);
  const hasMore = group.documents.length > ITEMS_TO_SHOW;
  const hiddenCount = group.documents.length - ITEMS_TO_SHOW;
  
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Group header */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/30">
        <div className="flex items-center gap-2">
          <span>{group.icon}</span>
          <span className="font-medium text-foreground">
            {group.label}
          </span>
          <span className="text-sm text-muted-foreground">
            ({group.documents.length})
          </span>
        </div>
      </div>
      
      {/* Documents */}
      <div className="divide-y divide-gray-100 dark:divide-slate-700">
        {visibleDocs.map(doc => (
          <DocumentCard
            key={doc.documentId}
            document={doc}
            onClick={() => onDocumentClick(doc)}
            onRelatedTagClick={onRelatedTagClick}
            onLinkToProject={onLinkToProject}
            onPasswordRequired={onPasswordRequired}
            onDelete={onDelete}
            onReindex={onReindex}
            onEdit={onEdit}
            isReindexing={reindexingDocId === doc.documentId}
            showRelevanceScore={showRelevanceScore}
          />
        ))}
      </div>
      
      {/* Show more/less button */}
      {hasMore && (
        <Button
          variant="ghost"
          onClick={onToggleShowAll}
          className="w-full py-2 text-sm font-medium text-muted-foreground rounded-none"
        >
          {showAll ? '▲ Show less' : `▼ Show all ${group.documents.length} (${hiddenCount} more)`}
        </Button>
      )}
    </div>
  );
}

// Document card component
interface DocumentCardProps {
  document: ReferenceDocumentCard;
  onClick: () => void;
  onRelatedTagClick: (tag: { documentId: string; title: string; standardNumber?: string }) => void;
  onLinkToProject?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onPasswordRequired?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onDelete?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onReindex?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  onEdit?: (doc: ReferenceDocumentCard, e: React.MouseEvent) => void;
  isReindexing?: boolean;
  showRelevanceScore: boolean;
}

function DocumentCard({ 
  document, 
  onClick, 
  onRelatedTagClick,
  onLinkToProject,
  onPasswordRequired,
  onDelete,
  onReindex,
  onEdit,
  isReindexing,
  showRelevanceScore,
}: DocumentCardProps) {
  const [showAllTags, setShowAllTags] = useState(false);
  
  const relatedDocs = document.relatedDocuments || [];
  const visibleTags = showAllTags ? relatedDocs : relatedDocs.slice(0, 4);
  const hasMoreTags = relatedDocs.length > 4;
  
  // Get display title - never show ref_* IDs
  const getDisplayTitle = () => {
    const standardNum = document.standardNumber;
    const extractedFromText = extractStandardFromText(document.matchedPassage) || 
      extractStandardFromText(document.aiSummary) ||
      extractStandardFromText(document.title);
    const titleIsRefId = document.title?.startsWith('ref_');
    
    if (standardNum && !standardNum.startsWith('ref_')) {
      return standardNum;
    } else if (extractedFromText) {
      return extractedFromText;
    } else if (!titleIsRefId && document.title) {
      return document.title;
    }
    return 'Reference Document';
  };
  
  // Suppress unused props (removed from simplified card)
  void onLinkToProject;
  void isReindexing;
  
  return (
    <div
      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      {/* Simplified card: Document Number, Title, Revision, Date */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Document Number (prominent) */}
          <h4 className="font-semibold text-foreground text-lg">{getDisplayTitle()}</h4>
          
          {/* Title (if different from document number) */}
          {document.title && document.title !== getDisplayTitle() && !document.title.startsWith('ref_') && (
            <p className="text-sm text-foreground mt-0.5">{document.title}</p>
          )}
          
          {/* Metadata row: Revision, Category, Date */}
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            {document.version && (
              <span>Rev: {document.version}</span>
            )}
            <span className={`px-2 py-0.5 rounded ${
              document.documentType === 'industry-standard'
                ? 'bg-secondary'
                : 'bg-info-light text-info'
            }`}>
              {document.documentType === 'industry-standard' ? 'Industry Standard' : 'Reference'}
            </span>
            {document.createdAt && (
              <span>{new Date(document.createdAt).toLocaleDateString()}</span>
            )}
          </div>
        </div>
        
        {/* Actions - admin only */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Edit button */}
          {onEdit && (
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => onEdit(document, e)}
              className="p-1.5 text-muted-foreground hover:text-foreground"
              title="Edit metadata"
            >
              <span className="text-sm">✏️</span>
            </Button>
          )}
          
          {/* Reindex button - only show for failed status */}
          {onReindex && document.indexStatus === 'failed' && (
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => onReindex(document, e)}
              className="p-1.5 text-muted-foreground hover:text-foreground"
              title="Retry indexing"
            >
              <span className="text-sm">↻</span>
            </Button>
          )}
          
          {/* Delete button */}
          {onDelete && (
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => onDelete(document, e)}
              className="p-1.5 text-muted-foreground hover:text-destructive"
              title="Delete document"
            >
              <span className="text-sm">🗑</span>
            </Button>
          )}
          
          {/* Password required indicator */}
          {document.indexStatus === 'password_required' && onPasswordRequired && (
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onPasswordRequired(document, e);
              }}
              className="text-xs px-2 py-0.5 bg-warning-light text-warning hover:bg-warning/20"
              title={document.errorMessage || 'PDF requires password'}
            >
              🔒 Enter Password
            </Button>
          )}
        </div>
      </div>
      
      {/* AI Summary or matched passage - show more content for search results */}
      {(document.aiSummary || document.matchedPassage) && (
        <p className={`text-sm text-muted-foreground mb-3 ${
          showRelevanceScore ? 'line-clamp-4' : 'line-clamp-2'
        }`}>
          {document.matchedPassage || document.aiSummary}
        </p>
      )}
      
      {/* Related document tags */}
      {relatedDocs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" onClick={e => e.stopPropagation()}>
          {visibleTags.map(tag => (
            <Button
              key={tag.documentId}
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onRelatedTagClick(tag);
              }}
              className="h-auto py-0.5 px-2 text-xs bg-info-light text-info hover:bg-info"
              title={`Open ${tag.title}`}
            >
              {tag.standardNumber || tag.title}
            </Button>
          ))}
          {hasMoreTags && !showAllTags && (
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                setShowAllTags(true);
              }}
              className="h-auto py-0.5 px-2 text-xs bg-muted/30 text-muted-foreground hover:bg-muted/50"
            >
              +{relatedDocs.length - 4} more
            </Button>
          )}
        </div>
      )}
      
      {/* Linked projects */}
      {document.linkedProjects && document.linkedProjects.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2" onClick={e => e.stopPropagation()}>
          <span className="text-xs text-muted-foreground">🔗</span>
          {document.linkedProjects.slice(0, 3).map((proj, idx) => {
            // Handle both string (projectId) and object format
            const isString = typeof proj === 'string';
            const displayName = isString ? proj.slice(0, 8) + '...' : (proj.projectCode || proj.projectName);
            const key = isString ? proj : proj.projectId;
            return (
              <span
                key={key || idx}
                className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded"
                title={isString ? proj : proj.projectName}
              >
                {displayName}
              </span>
            );
          })}
          {document.linkedProjects.length > 3 && (
            <span className="text-xs text-muted-foreground">
              +{document.linkedProjects.length - 3} more
            </span>
          )}
        </div>
      )}
      
      {/* Metadata footer */}
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        {document.organization && (
          <span>{document.organization}</span>
        )}
        {document.chunkCount && (
          <span>{document.chunkCount} chunks indexed</span>
        )}
        {document.lastAccessedAt && (
          <span>Last opened {formatRelativeTime(document.lastAccessedAt)}</span>
        )}
      </div>
    </div>
  );
}

// Helper to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
